import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, Check, Crown, Zap, Users, Star, 
  ArrowRight, Loader2, X, AlertCircle, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const planFeatures = {
  free: [
    { text: '5 kupones/mes', included: true },
    { text: 'Perfil público básico', included: true },
    { text: 'Analíticas limitadas', included: true },
    { text: 'Soporte por email', included: false },
    { text: 'Listado destacado', included: false },
    { text: 'Marketing con IA', included: false }
  ],
  starter: [
    { text: '10 kupones/mes', included: true },
    { text: 'Perfil público completo', included: true },
    { text: 'Analíticas básicas', included: true },
    { text: 'Soporte por email', included: true },
    { text: 'Listado destacado', included: true },
    { text: 'Marketing con IA', included: false }
  ],
  pro: [
    { text: '30 kupones/mes', included: true },
    { text: 'Perfil público premium', included: true },
    { text: 'Analíticas avanzadas + QR', included: true },
    { text: 'Soporte prioritario', included: true },
    { text: 'Listado destacado', included: true },
    { text: 'Marketing con IA', included: true }
  ],
  business: [
    { text: 'Kupones ilimitados', included: true },
    { text: 'Perfil personalizado', included: true },
    { text: 'Todas las analíticas', included: true },
    { text: 'Soporte dedicado 24/7', included: true },
    { text: 'Máxima visibilidad', included: true },
    { text: 'Marketing con IA + API', included: true }
  ]
};

const planIcons = {
  free: <Users className="w-6 h-6" />,
  starter: <Zap className="w-6 h-6" />,
  pro: <Crown className="w-6 h-6" />,
  business: <Star className="w-6 h-6" />
};

const planColors = {
  free: {
    bg: 'bg-gray-50 dark:bg-gray-900',
    border: 'border-gray-200 dark:border-gray-700',
    icon: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    button: 'bg-gray-200 text-gray-600 cursor-default'
  },
  starter: {
    bg: 'bg-blue-50 dark:bg-blue-950/20',
    border: 'border-blue-200 dark:border-blue-700',
    icon: 'bg-blue-500 text-white',
    button: 'bg-blue-600 hover:bg-blue-700 text-white'
  },
  pro: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/20',
    border: 'border-emerald-300 dark:border-emerald-700',
    icon: 'bg-emerald-500 text-white',
    button: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    popular: true
  },
  business: {
    bg: 'bg-purple-50 dark:bg-purple-950/20',
    border: 'border-purple-200 dark:border-purple-700',
    icon: 'bg-purple-500 text-white',
    button: 'bg-purple-600 hover:bg-purple-700 text-white'
  }
};

export const SubscriptionCheckout = ({ businessId, currentPlan = 'free', onUpgrade }) => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    if (plan.id === 'free' || plan.id === currentPlan) return;
    setSelectedPlan(plan);
    setShowModal(true);
  };

  const handleCheckout = async () => {
    if (!selectedPlan) return;
    
    setCheckingOut(selectedPlan.id);
    try {
      const response = await axios.post(
        `${API}/subscriptions/checkout`,
        {
          plan_id: selectedPlan.id,
          success_url: `${window.location.origin}/business/dashboard?subscription=success`,
          cancel_url: `${window.location.origin}/business/dashboard?subscription=cancelled`
        },
        {
          headers: { 'X-Business-Id': businessId }
        }
      );

      if (response.data.success && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error(response.data.error || 'Error iniciando checkout');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error procesando suscripción');
    } finally {
      setCheckingOut(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div data-testid="subscription-checkout">
      {/* Current Plan Banner */}
      <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">Tu plan actual</p>
            <p className="font-bold text-emerald-800 dark:text-emerald-200 capitalize">{currentPlan}</p>
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${planColors[currentPlan]?.icon}`}>
            {planIcons[currentPlan]}
          </div>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => {
          const colors = planColors[plan.id] || planColors.free;
          const features = planFeatures[plan.id] || [];
          const isCurrentPlan = plan.id === currentPlan;
          const isDowngrade = plans.findIndex(p => p.id === currentPlan) > plans.findIndex(p => p.id === plan.id);
          
          return (
            <motion.div
              key={plan.id}
              whileHover={{ y: -4 }}
              className={`relative rounded-2xl border-2 p-5 ${colors.bg} ${colors.border} ${
                colors.popular ? 'ring-2 ring-emerald-500 ring-offset-2' : ''
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {colors.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Popular
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.icon}`}>
                  {planIcons[plan.id]}
                </div>
                <div>
                  <h3 className="font-bold">{plan.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {plan.kupons_per_month === -1 ? 'Ilimitado' : `${plan.kupons_per_month} kupones/mes`}
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <span className="text-3xl font-bold">{plan.price}€</span>
                <span className="text-muted-foreground text-sm">/mes</span>
              </div>

              <ul className="space-y-2 mb-6">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    {feature.included ? (
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                    )}
                    <span className={feature.included ? '' : 'text-muted-foreground'}>{feature.text}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleSelectPlan(plan)}
                disabled={isCurrentPlan || plan.id === 'free' || isDowngrade || checkingOut === plan.id}
                className={`w-full ${isCurrentPlan ? 'bg-gray-200 text-gray-500 cursor-default' : colors.button}`}
                data-testid={`select-plan-${plan.id}`}
              >
                {checkingOut === plan.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isCurrentPlan ? (
                  'Plan actual'
                ) : plan.id === 'free' ? (
                  'Gratis'
                ) : isDowngrade ? (
                  'No disponible'
                ) : (
                  <>
                    Seleccionar <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </motion.div>
          );
        })}
      </div>

      {/* Checkout Confirmation Modal */}
      <AnimatePresence>
        {showModal && selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl p-6 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="checkout-modal"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${planColors[selectedPlan.id]?.icon}`}>
                  {planIcons[selectedPlan.id]}
                </div>
                <div>
                  <h3 className="font-bold text-lg">Actualizar a {selectedPlan.name}</h3>
                  <p className="text-muted-foreground">{selectedPlan.price}€/mes</p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-4">
                <p className="text-sm text-muted-foreground mb-2">Tu suscripción incluirá:</p>
                <ul className="space-y-1">
                  {(planFeatures[selectedPlan.id] || []).filter(f => f.included).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {feature.text}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    Se te redirigirá a Stripe para completar el pago de forma segura. 
                    Puedes cancelar tu suscripción en cualquier momento.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCheckout}
                  disabled={checkingOut}
                  className={`flex-1 ${planColors[selectedPlan.id]?.button}`}
                  data-testid="confirm-checkout-btn"
                >
                  {checkingOut ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pagar {selectedPlan.price}€/mes
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionCheckout;
